/**
 * The password rule for the sign-up and reset forms (SGPW-2132, following SGPW-2003).
 *
 * The user microservice enforces the same rule and answers with a specific reason when it refuses
 * a password, so this is a convenience that tells the person before they submit. Keep it in step
 * with the main site's Contentful rule and with the microservice.
 *
 * The maximum is enforced here but never shown to users, so it appears in no message. There is
 * deliberately no `g` flag: a global regular expression remembers where its last match ended, so
 * testing the same pattern twice can give different answers.
 *
 * The login form applies no password rule: an existing password may predate this policy.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 100;

export const PASSWORD_PATTERN = new RegExp(
    `^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?!.*(.)\\1\\1).{${PASSWORD_MIN_LENGTH},${PASSWORD_MAX_LENGTH}}$`
);

/** The same wording as the main SailGP site shows under its password fields. */
export const PASSWORD_RULE_MESSAGE =
    `Password must be at least ${PASSWORD_MIN_LENGTH} characters and include an uppercase letter, a lowercase letter, a number and a special character. ` +
    'Do not repeat a character three times in a row, and avoid common words, your name or your email address.';

/** Shown on the reset form when the server gives no reason of its own. */
export const RESET_FAILURE_MESSAGE =
    'There has been a problem resetting your password. It cannot repeat any of your last 3 passwords or contain your name.';

export interface ServerRefusal {
    message: string;
    /** True when the reason is about the password itself, so it belongs next to the password field. */
    onPasswordField: boolean;
}

interface ErrorEntry {
    path?: unknown;
    message?: unknown;
}

const isText = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/** "newPassword: Password is too long." becomes "Password is too long." */
const withoutFieldPrefix = (message: string): string => message.replace(/^[A-Za-z0-9_.]+:\s+/, '');

/** Text that came straight from Oracle Identity Cloud / Oracle Integration rather than from us. */
const RAW_UPSTREAM_TEXT =
    /https?:\/\/|<!\[CDATA\[|InboundJaxrsResponse|urn:ietf|MePasswordResetter|Bad Request|"schemas"/i;

const CHOOSE_AGAIN = 'Please choose a different one.';

/** Shown when the identity service refuses a password for a reason we do not have wording for. */
const PASSWORD_REFUSED_MESSAGE = `This password does not meet the password requirements. ${CHOOSE_AGAIN}`;

/** One sentence for every naming rule, so the person is not told which of their names was caught. */
const NAME_RULE = /first name|last name|user ?name/i;
const NAME_RULE_MESSAGE = `Your password cannot contain your first name, last name or user name. ${CHOOSE_AGAIN}`;

/**
 * Turns the identity service's raw error text (a password-policy refusal wrapped in URLs, CDATA
 * and JSON) into a short sentence for the password field. Returns undefined when the text is not
 * a password refusal, so the caller shows its own fallback instead of the raw text.
 */
const refusalFromRawText = (text: string): ServerRefusal | undefined => {
    if (!/pwdpolicyViolation|password policy|does not meet the criteria/i.test(text)) return undefined;
    const because = /because:\s*\[([^\]]*)\]/i.exec(text);
    return {
        message: because && NAME_RULE.test(because[1]) ? NAME_RULE_MESSAGE : PASSWORD_REFUSED_MESSAGE,
        onPasswordField: true,
    };
};

/**
 * The reason the server gave for refusing a sign-up or reset, or undefined when there is none
 * (a success, a network failure, a body we do not recognise), so the caller can show its own
 * fallback. Understands the user microservice's current body (`errors` with a `path` per reason,
 * plus `message`) and the older `errorDescription` and `error_description` shapes. Raw upstream
 * text in the older shapes is never returned as it is: a password refusal is put into plain words,
 * anything else is dropped.
 */
export const getServerRefusal = (
    body: unknown,
    passwordField: 'password' | 'newPassword'
): ServerRefusal | undefined => {
    if (!body || typeof body !== 'object') return undefined;
    const response = body as Record<string, unknown>;
    if (typeof response.status !== 'string' || response.status === 'success') return undefined;

    const entries = (Array.isArray(response.errors) ? response.errors : []) as ErrorEntry[];
    const forPassword = entries.filter((e) => e && e.path === passwordField && isText(e.message));
    if (forPassword.length > 0) {
        return {
            message: forPassword.map((e) => (e.message as string).trim()).join(' '),
            onPasswordField: true,
        };
    }

    const other = entries.find((e) => e && isText(e.message));
    if (other) return {message: (other.message as string).trim(), onPasswordField: false};

    for (const field of [response.message, response.errorDescription, response.error_description]) {
        if (!isText(field)) continue;
        const text = field.trim();
        if (!RAW_UPSTREAM_TEXT.test(text)) return {message: withoutFieldPrefix(text), onPasswordField: false};
        const refusal = refusalFromRawText(text);
        if (refusal) return refusal;
    }
    return undefined;
};
